<template>
  <div class="dq-ws-missing">
    <div class="dq-ws-missing__bg" aria-hidden="true"></div>
    <div class="container py-5">
      <div class="row justify-content-center">
        <div class="col-lg-7 col-xl-6">
          <div class="dq-ws-missing__card">
            <div class="d-flex align-items-center gap-3 mb-4">
              <hr-payroll-mark variant="full" />
            </div>

            <div class="dq-ws-missing__badge mb-3">
              <i class="ti ti-building-off"></i>
              Workspace not found
            </div>

            <h1 class="dq-ws-missing__title mb-3">
              We couldn’t find
              <span v-if="slug">“{{ slug }}”</span>
              <span v-else>that workspace</span>
            </h1>

            <p class="dq-ws-missing__copy mb-4">
              The address
              <strong>{{ hostLabel }}</strong>
              doesn’t match an active DoneQuick HR workspace. Double-check the
              spelling, or enter the correct workspace below.
            </p>

            <div class="dq-ws-missing__actions d-grid gap-2 mb-3">
              <button
                type="button"
                class="btn btn-primary btn-lg"
                @click="openWorkspaceModal"
              >
                Enter workspace URL
                <i class="ti ti-arrow-right ms-2"></i>
              </button>
              <a :href="marketingUrl" class="btn btn-outline-secondary btn-lg">
                Back to DoneQuick HR home
              </a>
            </div>

            <p class="text-muted small mb-0">
              Looking to create a company account?
              <a :href="registerUrl" class="fw-semibold">Start a free trial</a>
            </p>
          </div>
        </div>
      </div>
    </div>

    <workspace-sign-in-modal
      ref="signInModal"
      modal-id="workspace_not_found_sign_in"
    />
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import { useRoute } from "vue-router";
import HrPayrollMark from "@/components/branding/hr-payroll-mark.vue";
import WorkspaceSignInModal from "@/components/auth/workspace-sign-in-modal.vue";
import {
  centralMarketingUrl,
  currentHostname,
  tenantSlugFromHost,
} from "@/utils/hostname";

const route = useRoute();
const signInModal = ref(null);

const slug = computed(
  () => route.query.slug || tenantSlugFromHost() || ""
);
const hostLabel = computed(() => currentHostname());
const marketingUrl = computed(() => centralMarketingUrl("/"));
const registerUrl = computed(() => centralMarketingUrl("/register"));

function openWorkspaceModal() {
  signInModal.value?.open(slug.value || "");
}

onMounted(() => {
  document.body.classList.add("bg-white");
});
</script>

<style scoped>
.dq-ws-missing {
  min-height: 100vh;
  position: relative;
  display: flex;
  align-items: center;
}

.dq-ws-missing__bg {
  position: absolute;
  inset: 0;
  background:
    radial-gradient(circle at top right, rgba(254, 138, 34, 0.14), transparent 42%),
    radial-gradient(circle at bottom left, rgba(14, 165, 233, 0.1), transparent 40%),
    linear-gradient(180deg, #fffaf5 0%, #f8fafc 100%);
  z-index: 0;
}

.dq-ws-missing__card {
  position: relative;
  z-index: 1;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid rgba(15, 23, 42, 0.06);
  border-radius: 1.35rem;
  padding: 2rem;
  box-shadow: 0 24px 50px rgba(15, 23, 42, 0.08);
  backdrop-filter: blur(8px);
}

.dq-ws-missing__badge {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.35rem 0.75rem;
  border-radius: 999px;
  background: rgba(239, 68, 68, 0.08);
  color: #b91c1c;
  font-size: 0.82rem;
  font-weight: 600;
}

.dq-ws-missing__title {
  font-size: clamp(1.65rem, 3vw, 2.1rem);
  font-weight: 750;
  color: #0f172a;
  line-height: 1.2;
}

.dq-ws-missing__copy {
  color: #64748b;
  font-size: 1.02rem;
  line-height: 1.6;
}

@media (max-width: 575.98px) {
  .dq-ws-missing__card {
    padding: 1.35rem;
  }
}
</style>
